import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Mail,
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  KeyRound,
} from "lucide-react";
import { API_URL } from "./Config/Env";
import Swal from "sweetalert2";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Email Required",
        text: "Please enter the email address associated with your account.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    setSending(true);

    try {
      const response = await axios.post(
        `${API_URL}/request_reset/`,
        {
          email: email.trim(),
        }
      );

      await Swal.fire({
        icon: "success",
        title: "Check Your Email",
        text:
          response.data.message ||
          "We've sent password reset instructions to your email address.",
        confirmButtonColor: "#2563EB",
      });

      setEmail("");

      navigate("/signin");
    } catch (error) {
      console.error("Password reset error:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "We couldn't send the password reset link. Please try again.";

      Swal.fire({
        icon: "error",
        title: "Unable to Send Reset Link",
        text: errorMessage,
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="min-h-screen grid lg:grid-cols-2">

        {/* Left Brand Section */}
        <section className="relative hidden lg:flex bg-slate-950 text-white overflow-hidden">

          {/* Background Glow */}
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-600/25 rounded-full blur-[150px]" />

          <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[140px]" />

          {/* Background Grid */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
              `,
              backgroundSize: "50px 50px",
            }}
          />

          <div className="relative z-10 flex flex-col justify-between w-full p-12 xl:p-16">

            {/* Logo */}
            <Link
              to="/"
              className="inline-flex items-center gap-3 w-fit"
            >
              <img
                src="/logo.png"
                alt="Nexindi Tech"
                className="w-12 h-12 rounded-xl object-cover border border-white/20"
              />

              <div>
                <p className="font-bold text-xl tracking-tight">
                  NEXINDI
                </p>

                <p className="text-xs tracking-[0.25em] uppercase text-blue-400">
                  Tech
                </p>
              </div>
            </Link>

            {/* Main Content */}
            <div className="max-w-xl py-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-7">
                <KeyRound size={15} />
                Account Recovery
              </div>

              <h1 className="text-5xl xl:text-6xl font-bold tracking-tight leading-[1.08] mb-7">
                Recover your account
                <span className="block text-blue-400">
                  securely and quickly.
                </span>
              </h1>

              <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-lg">
                Enter the email connected to your Nexindi account and
                we'll send you instructions to reset your password securely.
              </p>

              <div className="space-y-4">
                {[
                  "Secure password recovery",
                  "Reset instructions sent by email",
                  "Return to your account quickly",
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 text-slate-300"
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-400/20 flex items-center justify-center">
                      <CheckCircle2
                        size={16}
                        className="text-blue-400"
                      />
                    </div>

                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-sm text-slate-500">
              Empowering Africa through technology.
            </p>
          </div>
        </section>

        {/* Reset Form Section */}
        <section className="relative flex items-center justify-center px-6 py-12 md:px-12 bg-white">

          {/* Mobile Glow */}
          <div className="lg:hidden absolute top-0 left-0 w-[300px] h-[300px] bg-blue-100 rounded-full blur-[120px] opacity-60" />

          <div className="relative z-10 w-full max-w-md">

            {/* Mobile Logo */}
            <Link
              to="/"
              className="lg:hidden flex items-center justify-center gap-3 mb-12"
            >
              <img
                src="/logo.png"
                alt="Nexindi Tech"
                className="w-12 h-12 rounded-xl object-cover"
              />

              <div>
                <p className="font-bold text-xl text-slate-900 leading-tight">
                  NEXINDI
                </p>

                <p className="text-[10px] text-blue-600 uppercase tracking-[0.22em]">
                  Tech
                </p>
              </div>
            </Link>

            {/* Back */}
            <Link
              to="/signin"
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 transition mb-9"
            >
              <ArrowLeft size={17} />
              Back to Sign In
            </Link>

            {/* Form Header */}
            <div className="mb-9">
              <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-6">
                <KeyRound size={25} />
              </div>

              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-3">
                Forgot your password?
              </h2>

              <p className="text-slate-500 leading-relaxed">
                No problem. Enter the email address associated with
                your account and we'll send you a password reset link.
              </p>
            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Email Address
                </label>

                <div className="relative">
                  <Mail
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white text-slate-900 outline-none transition-all duration-300 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={sending}
                className="group w-full flex items-center justify-center gap-3 bg-blue-600 text-white py-3.5 px-6 rounded-xl font-semibold hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-600/20 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
              >
                {sending ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending Reset Link...
                  </>
                ) : (
                  <>
                    Send Reset Link

                    <ArrowRight
                      size={18}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </form>

            {/* Sign in */}
            <div className="mt-8 pt-7 border-t border-slate-200 text-center">
              <p className="text-sm text-slate-500">
                Remembered your password?{" "}
                <Link
                  to="/signin"
                  className="font-semibold text-blue-600 hover:text-blue-700"
                >
                  Sign in
                </Link>
              </p>
            </div>

            {/* Security Note */}
            <div className="mt-7 flex items-start gap-3 rounded-2xl bg-slate-50 border border-slate-200 p-4">
              <ShieldCheck
                size={19}
                className="text-blue-600 flex-shrink-0 mt-0.5"
              />

              <p className="text-xs text-slate-500 leading-relaxed">
                For security, password reset instructions are only sent
                to the email address associated with your Nexindi account.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}